import { Badge, Card, EmptyState, ErrorState, Loading, formatDate, formatMoney } from "@corecrm/ui";
import { Link } from "react-router-dom";
import { useMe, useOpenTasks, useProjects, useWaiting } from "../api/queries";
import type { OpenTask } from "../api/types";
import { Status } from "../components/bits";
import { MarkDone, Prerequisites } from "../components/Tasks";
import { BILLING_MODEL } from "../lib/labels";
import { noProjectsText } from "./Projects";

/* What's waiting on the client (quotes, change requests, deliverables, feedback requests,
   invoices to pay), then their projects. The prototype's pHome(). */
export function HomePage() {
  const me = useMe().data!;
  const waiting = useWaiting();
  const projects = useProjects();
  const first = me.name.split(/\s+/)[0];

  const items = [
    ...waiting.quotes.map((q) => ({
      key: `q${q.id}`,
      title: `Review quote ${q.number}: ${q.title}`,
      sub: `${q.total ? `${formatMoney(q.total)} · ` : ""}valid until ${formatDate(q.valid_until)}`,
      action: (
        <Link className="btn sm primary" to={`/quotes/${q.id}`}>
          Review quote
        </Link>
      ),
    })),
    ...waiting.deliverables.map((d) => ({
      key: `d${d.id}`,
      title: `Approve deliverable: ${d.title}`,
      sub: `${d.phase_name} · submitted ${formatDate(d.submitted_at)}`,
      action: (
        <Link className="btn sm primary" to={`/deliverables/${d.id}`}>
          Review
        </Link>
      ),
    })),
    ...waiting.changeRequests.map((q) => ({
      key: `c${q.id}`,
      title: `Approve change request ${q.number}: ${q.title}`,
      sub: [
        q.project_id &&
          `Adds to ${projects.data?.find((p) => p.id === q.project_id)?.name ?? "your project"}`,
        q.total && `${formatMoney(q.total)} incl. tax`,
        `valid until ${formatDate(q.valid_until)}`,
      ]
        .filter(Boolean)
        .join(" · "),
      action: (
        <Link className="btn sm primary" to={`/quotes/${q.id}`}>
          Review change
        </Link>
      ),
    })),
    ...waiting.feedback.map((f) => ({
      key: `f${f.id}`,
      title: f.project_name ? `Tell us how we did on ${f.project_name}` : "Tell us how we did",
      sub: `Less than a minute · asked ${formatDate(f.created_at)}`,
      action: (
        <Link className="btn sm primary" to={`/feedback/${f.token}`}>
          Give feedback
        </Link>
      ),
    })),
    ...waiting.invoices.map((i) => ({
      key: `i${i.id}`,
      title: `Pay invoice ${i.number ?? ""}`,
      sub: `${formatMoney(i.balance)} · due ${formatDate(i.due_date)}${i.overdue ? " · overdue" : ""}`,
      action: i.pay_url ? (
        <a className="btn sm primary" href={i.pay_url} target="_blank" rel="noopener noreferrer">
          Pay
        </a>
      ) : (
        <Link className="btn sm" to={`/invoices/${i.id}`}>
          View
        </Link>
      ),
    })),
  ];

  return (
    <>
      <p className="muted">Welcome back, {first}. Here is what needs your attention.</p>
      <Card title="Waiting on you">
        {waiting.isPending ? (
          <Loading />
        ) : waiting.error ? (
          <ErrorState error={waiting.error} />
        ) : items.length ? (
          <ul className="todo">
            {items.map((it) => (
              <li key={it.key}>
                <div className="what">
                  <strong>{it.title}</strong>
                  <span>{it.sub}</span>
                </div>
                {it.action}
              </li>
            ))}
          </ul>
        ) : (
          <EmptyState>You are all caught up.</EmptyState>
        )}
      </Card>
      <OpenTasks />
      <Card title="Your projects">
        {projects.isPending ? (
          <Loading />
        ) : projects.error ? (
          <ErrorState error={projects.error} onRetry={() => projects.refetch()} />
        ) : projects.data.length ? (
          <div className="item-list">
            {projects.data.map((p) => (
              <div className="item" key={p.id}>
                <div className="what">
                  <Link to={`/projects/${p.id}`}>
                    <strong>{p.name}</strong>
                  </Link>
                  <span>{BILLING_MODEL[p.billing_model] ?? p.billing_model}</span>
                </div>
                <Status status={p.status} />
              </div>
            ))}
          </div>
        ) : (
          <EmptyState>{noProjectsText(me.billing_contact ?? false)}</EmptyState>
        )}
      </Card>
    </>
  );
}

type DueGroup = OpenTask["due_group"];

/** The due groups in the order Home shows them, with their headings. */
export const DUE_GROUPS: { key: DueGroup; label: string }[] = [
  { key: "overdue", label: "Overdue" },
  { key: "today", label: "Due today" },
  { key: "tomorrow", label: "Due tomorrow" },
  { key: "this_week", label: "Due in the next 7 days" },
  { key: "later", label: "Due later" },
  { key: "no_date", label: "No due date" },
];

/** Tasks grouped by project, in the order they came. */
export function byProject(tasks: OpenTask[]): { id: string; name: string; tasks: OpenTask[] }[] {
  const groups = new Map<string, { id: string; name: string; tasks: OpenTask[] }>();
  for (const t of tasks) {
    const g = groups.get(t.project_id) ?? { id: t.project_id, name: t.project_name, tasks: [] };
    g.tasks.push(t);
    groups.set(t.project_id, g);
  }
  return [...groups.values()];
}

/** Tasks grouped by when they're due (empty groups left out), then by project. */
export function byDue(tasks: OpenTask[]) {
  return DUE_GROUPS.map((g) => ({
    ...g,
    projects: byProject(tasks.filter((t) => t.due_group === g.key)),
  })).filter((g) => g.projects.length);
}

function dueNote(t: OpenTask) {
  if (!t.due_date || t.due_group === "today" || t.due_group === "tomorrow") return null;
  return t.due_group === "overdue" ? (
    <Badge kind="bad">Was due {formatDate(t.due_date)}</Badge>
  ) : (
    <Badge kind="info">Due {formatDate(t.due_date)}</Badge>
  );
}

/** Every open task assigned to you, by when it's due and then by project, to mark done here. */
function OpenTasks() {
  const open = useOpenTasks();
  return (
    <Card title="Your open tasks">
      {open.isPending ? (
        <Loading />
      ) : open.error ? (
        <ErrorState error={open.error} onRetry={() => open.refetch()} />
      ) : open.data.length ? (
        byDue(open.data).map((g) => (
          <div key={g.key} className="task-group due-group">
            <h3>
              {g.label}
              <span className="cnt">{g.projects.reduce((n, p) => n + p.tasks.length, 0)}</span>
            </h3>
            {g.projects.map((p) => (
              <div key={p.id} className="project-tasks">
                <h4>
                  <Link to={`/projects/${p.id}`}>{p.name}</Link>
                </h4>
                <ul className="todo">
                  {p.tasks.map((t) => (
                    <li key={t.id}>
                      <div className="what">
                        <strong>{t.title}</strong>
                        <span>
                          {dueNote(t)}
                          {t.description && ` ${t.description}`}
                        </span>
                        <Prerequisites task={t} />
                      </div>
                      <MarkDone task={t} />
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        ))
      ) : (
        <EmptyState>You have no open tasks.</EmptyState>
      )}
    </Card>
  );
}
